import type React from "react";

import { useEffect, useState } from "react";
import type { PermApiResponse, Roles } from "../../../../interface/interfaces";
import useAdminApiHook from "../../../../utils/useAdminApi";
import { toast } from "react-toastify";

interface UpdateRoleModalProps {
  role: Roles | null;
  isOpen: boolean;
  onClose: () => void;
}

interface PermissionCategory {
  title: string;
  permissions: string[];
}

const UpdateRoleModal = ({ role, isOpen, onClose }: UpdateRoleModalProps) => {
  const [selectedAdminPermissions, setSelectedAdminPermissions] = useState<
    string[]
  >([]);
  const [permissionCategories, setPermissionCategories] = useState<
    PermissionCategory[]
  >([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalAdminPermissions, setOriginalAdminPermissions] = useState<
    string[]
  >([]);
  const [availableAdminPermissions, setAvailableAdminPermissions] = useState<
    string[]
  >([]);

  const { data, makeRequest } = useAdminApiHook<PermApiResponse>(
    "/admin/system-role",
    {
      method: "get",
    }
  );

  const { isLoading, makeRequest: updateSystemRole } = useAdminApiHook(
    `/admin/update-role`,
    {
      method: "post",
    }
  );

  useEffect(() => {
    if (isOpen) {
      makeRequest();
    }
  }, [isOpen]);

  useEffect(() => {
    if (data?.data && role) {
      // Only get admin permissions
      const adminPerms = data.data.adminRolePermissions || [];
      setAvailableAdminPermissions(adminPerms);

      const categories: Record<string, string[]> = {};
      adminPerms.forEach((permission) => {
        const category = getCategoryFromPermission(permission);
        if (!categories[category]) {
          categories[category] = [];
        }
        categories[category].push(permission);
      });

      const formattedCategories = Object.entries(categories).map(
        ([title, perms]) => ({
          title,
          permissions: perms,
        })
      );

      setPermissionCategories(formattedCategories);

      // Set current admin permissions
      const currentAdminPerms = role.adminRolePermissions || [];
      setSelectedAdminPermissions([...currentAdminPerms]);
      setOriginalAdminPermissions([...currentAdminPerms]);
    }
  }, [data, role]);

  // Check for changes
  useEffect(() => {
    const adminPermsChanged = !(
      selectedAdminPermissions.length === originalAdminPermissions.length &&
      selectedAdminPermissions.every((perm) =>
        originalAdminPermissions.includes(perm)
      ) &&
      originalAdminPermissions.every((perm) =>
        selectedAdminPermissions.includes(perm)
      )
    );

    setHasChanges(adminPermsChanged);
  }, [selectedAdminPermissions, originalAdminPermissions]);

  const getCategoryFromPermission = (permission: string): string => {
    if (permission.includes("TRANSACTION")) return "Transactions";
    if (permission.includes("USER")) return "Users";
    if (permission.includes("CUSTOMER")) return "Customers";
    if (permission.includes("SETTLEMENT")) return "Settlements";
    if (permission.includes("ROLE")) return "Roles";
    if (permission.includes("INVITE")) return "Invitations";
    if (permission.includes("MERCHANT")) return "Merchants";
    if (permission.includes("BANK")) return "Banking";
    return "Other";
  };

  const formatPermissionLabel = (permission: string): string => {
    return permission
      .replace(/CAN_/g, "")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const handleAdminPermissionChange = (permission: string) => {
    setSelectedAdminPermissions((prev) => {
      if (prev.includes(permission)) {
        return prev.filter((p) => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!role) return;

    const payload: any = {
      roleReference: role.roleReference,
      isAdminRole: role.isAdminRole,
      isSystemRole: role.isSystemRole,
      isCustomRole: role.isCustomRole,
    };

    // Send admin permissions as rolePermissions
    if (selectedAdminPermissions.length > 0) {
      payload.rolePermissions = selectedAdminPermissions;
    }

    const response = await updateSystemRole(payload);

    if (response?.status === 200 || response?.status === 201) {
      toast("Role updated successfully");
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 3000);
    } else {
      toast.warning(response?.data?.description);
    }
  };

  if (!role || !isOpen) return null;

  // Check if we have any admin permissions available
  const hasAdminPermissions = availableAdminPermissions.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Update System Role - {role.roleName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            {/* Admin Permissions - Only show if available */}
            {hasAdminPermissions ? (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                  Admin Permissions ({selectedAdminPermissions.length})
                </h3>
                <div className="max-h-96 overflow-y-auto space-y-4">
                  {permissionCategories.map((category) => {
                    const availableInCategory = category.permissions.filter(
                      (perm) => availableAdminPermissions.includes(perm)
                    );

                    if (availableInCategory.length === 0) return null;

                    return (
                      <div
                        key={`admin-${category.title}`}
                        className="space-y-2"
                      >
                        <h4 className="font-medium text-gray-700 text-sm">
                          {category.title}
                        </h4>
                        <div className="space-y-1 pl-2">
                          {availableInCategory.map((permission) => (
                            <div
                              key={`admin-${permission}`}
                              className="flex items-center"
                            >
                              <input
                                type="checkbox"
                                id={`admin-${permission}`}
                                checked={selectedAdminPermissions.includes(
                                  permission
                                )}
                                onChange={() =>
                                  handleAdminPermissionChange(permission)
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor={`admin-${permission}`}
                                className="ml-2 text-sm text-gray-700"
                              >
                                {formatPermissionLabel(permission)}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  No admin permissions available for this role.
                </p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !hasChanges}
              className={`px-4 py-2 text-white rounded-md transition-colors flex items-center justify-center ${
                isLoading || !hasChanges
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateRoleModal;
